import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';

import { selectAuthUser } from '../../redux/auth/auth.selectors';
import { signOut } from '../../redux/auth/auth.actions';
import {
    invitationVerifyRequest,
    invitationAcceptRequest,
    invitationReset,
} from '../../redux/invitation/invitation.actions';
import {
    selectInvitationData,
    selectInvitationIsLoading,
    selectInvitationError,
    selectInvitationErrorCode,
    selectInvitationAcceptStatus,
} from '../../redux/invitation/invitation.selectors';

import LoaderSimple from '../../components/Loaders/LoaderSimple/LoaderSimple.js';
import Logo2 from '../../components/Logo/Logo2';
import OnboardingUserMenu from '../Onboarding/OnboardingUserMenu';
import OnboardingBackToApp from '../Onboarding/OnboardingBackToApp';

import './Auth.scss';


const ERROR_TITLES = {
    NOT_FOUND: 'Invitation not found',
    ALREADY_ACCEPTED: 'Invitation already accepted',
    EXPIRED: 'Invitation expired',
    REVOKED: 'Invitation revoked',
    UNKNOWN: 'Invitation unavailable',
};

function deriveViewState({ token, isLoading, error, currentUser, invitationData }) {
    if (!token) return 'error';
    if (isLoading && !invitationData && !error) return 'loading';
    if (error) return 'error';
    if (!invitationData) return 'loading';
    if (!currentUser) return 'not-logged-in';
    if (currentUser.email !== invitationData.invitee_email) return 'wrong-email';
    return 'ready-to-accept';
}


const Invitation = () => {
    document.title = 'DB Design';

    const dispatch = useDispatch();
    const history = useHistory();
    const { token } = useParams();

    const currentUser = useSelector(selectAuthUser);
    const invitationData = useSelector(selectInvitationData);
    const isLoading = useSelector(selectInvitationIsLoading);
    const error = useSelector(selectInvitationError);
    const errorCode = useSelector(selectInvitationErrorCode);
    const acceptStatus = useSelector(selectInvitationAcceptStatus);

    // Set by "log in as a different account" so the effect below can defer
    // navigation until sign-out has actually cleared currentUser.
    const pendingLoginRedirectRef = useRef(false);

    // Verify token on mount; reset on unmount.
    useEffect(() => {
        if (token) dispatch(invitationVerifyRequest(token));
        return () => { dispatch(invitationReset()); };
    }, [token, dispatch]);

    // Once the saga has activated the new workspace, take the user to the app.
    useEffect(() => {
        if (acceptStatus === 'success') history.push('/diagrams/');
    }, [acceptStatus, history]);

    // handleLogoutAndLogin defers navigation to here. Sign-out is async — it only
    // clears currentUser on SIGN_OUT_SUCCESS (after the network round-trip), so
    // pushing to /login while still authenticated makes SignIn bounce straight
    // back here via its invite-param redirect (the URL appears not to change).
    // Wait until we're actually logged out, then go to login with the token.
    useEffect(() => {
        if (pendingLoginRedirectRef.current && !currentUser) {
            pendingLoginRedirectRef.current = false;
            history.push(`/login?invite=${token}`);
        }
    }, [currentUser, history, token]);

    const goBack = () => history.push(currentUser ? '/diagrams/' : '/login');
    const goToLoginWithInvite = () => history.push(`/login?invite=${token}`);
    const handleAccept = () => dispatch(invitationAcceptRequest(token));
    const handleLogoutAndLogin = () => {
        // Sign-out is async (currentUser clears on SIGN_OUT_SUCCESS, after the
        // network round-trip). Flag the intent and let the effect above navigate
        // once we're actually logged out — pushing now would land on /login while
        // still authenticated and SignIn would bounce us back here.
        pendingLoginRedirectRef.current = true;
        dispatch(signOut());
    };

    const viewState = deriveViewState({
        token, isLoading, error, currentUser, invitationData,
    });

    const shell = (inner) => (
        <div className="invitation-page sign-in-box">
            {/* <Logo2 isLand={true} /> */}
            {/* Both self-guard on currentUser, so they only render for logged-in
                viewers (wrong-email / ready-to-accept states). */}
            <OnboardingBackToApp />
            <OnboardingUserMenu />
            <div className="sign-in-wrap">
                <div className="sign-in">{inner}</div>
            </div>
        </div>
    );

    if (viewState === 'loading') {
        return null;
        // return shell(
        //     <>
        //         <div className="sign-in-title">Verifying invitation</div>
        //         <div className="invitation-loader"><LoaderSimple /></div>
        //     </>
        // );
    }

    if (viewState === 'error') {
        const title = ERROR_TITLES[errorCode] || ERROR_TITLES.UNKNOWN;
        return shell(
            <>
                <div className="sign-in-title">{title}</div>
                <p className="invitation-text">
                    If this seems incorrect or you are having issues accessing the workspace, please contact the workspace admins or support.
                </p>
                <button className="auth-btn-back" type="button" onClick={goBack}>Go back</button>
            </>
        );
    }

    const { workspace_name, inviter_name, invitee_email } = invitationData;

    if (viewState === 'not-logged-in') {
        return shell(
            <>
                <div className="sign-in-title">{`${inviter_name} has invited you to ${workspace_name}`}</div>
                <p className="invitation-text">DB Design helps teams visually design databases, schemas, and ER diagrams.</p>
                <div className="horizontal-line" />
                <div className="invitation-text-bottom">
                    {'To accept the invitation please log in as '}
                    <span className="bold">{invitee_email}</span>
                </div>
                <button className="auth-btn-back" type="button" onClick={goToLoginWithInvite}>Log in</button>
            </>
        );
    }

    if (viewState === 'wrong-email') {
        return shell(
            <>
                <div className="sign-in-title">{`${inviter_name} has invited you to ${workspace_name}`}</div>
                <p className="invitation-text">DB Design helps teams visually design databases, schemas, and ER diagrams.</p>
                <div className="horizontal-line" />
                <div className="invitation-text-bottom">
                    {/* {`You are signed in as ${currentUser.email}. Sign in as `} */}
                    {/* {' to accept.'} */}
                    {'To accept the invitation please log in as '}
                    <span className="bold">{invitee_email}</span>
                </div>
                <button className="auth-btn-back" type="button" onClick={handleLogoutAndLogin}>Log in</button>
            </>
        );
    }

    // ready-to-accept
    const acceptFailed = acceptStatus === 'error';
    return shell(
        <>
            <div className="sign-in-title">{`${inviter_name} has invited you to ${workspace_name}`}</div>
            <p className="invitation-text">DB Design helps teams visually design databases, schemas, and ER diagrams.</p>
            <button
                className="auth-btn-back"
                type="button"
                disabled={isLoading}
                onClick={handleAccept}
            >
                {isLoading ? <LoaderSimple /> : (acceptFailed ? 'Try again' : 'Accept invite')}
            </button>
            {acceptFailed && (
                <p className="invitation-error-text">{error?.message || 'Failed to accept invitation. Please try again.'}</p>
            )}
        </>
    );
};

export default Invitation;
